import { httpClient } from '@/core/api/http-client'
import type { Paged } from '@/domain/entities/paging'
import type {
  PaymentMethod,
  PaymentMethodInput,
  PaymentMethodListParams,
} from '@/domain/entities/payment-method.entity'
import type { PaymentMethodRepository } from '@/domain/repositories/payment-method.repository'
import { unwrap, unwrapPaged } from '@/infrastructure/shared/unwrap'
import type { PaymentMethodDto } from './payment-method.dto'
import { paymentMethodMapper } from './payment-method.mapper'

export class PaymentMethodHttpRepository implements PaymentMethodRepository {
  async list(outletId: string, params: PaymentMethodListParams): Promise<Paged<PaymentMethod>> {
    const raw = await httpClient.get<unknown>('/payment-methods', {
      params: {
        // BE menjawab 400 kalau outlet_id absen — daftar metode bayar tanpa
        // outlet memang tidak punya arti.
        outlet_id: outletId,
        page: params.page,
        limit: params.limit,
        search: params.search || undefined,
        // Absen = semua (aktif & nonaktif). Jangan kirim string kosong: BE
        // memakai strconv.ParseBool dan menolak nilai yang tidak dikenal.
        active: params.active ?? undefined,
      },
    })
    const paged = unwrapPaged<PaymentMethodDto>(raw, params.page, params.limit)
    return { ...paged, items: paged.items.map(paymentMethodMapper.toPaymentMethod) }
  }

  async create(outletId: string, input: PaymentMethodInput): Promise<PaymentMethod> {
    const raw = await httpClient.post<unknown>('/payment-methods', {
      outlet_id: outletId,
      ...paymentMethodMapper.toBody(input),
    })
    return paymentMethodMapper.toPaymentMethod(unwrap<PaymentMethodDto>(raw))
  }

  async update(id: string, input: PaymentMethodInput): Promise<PaymentMethod> {
    const raw = await httpClient.put<unknown>(
      `/payment-methods/${id}`,
      paymentMethodMapper.toBody(input),
    )
    return paymentMethodMapper.toPaymentMethod(unwrap<PaymentMethodDto>(raw))
  }

  async remove(id: string): Promise<void> {
    await httpClient.delete(`/payment-methods/${id}`)
  }

  async restore(id: string): Promise<PaymentMethod> {
    // Partial update: hanya `active` yang dikirim, sisanya dibiarkan apa adanya.
    const raw = await httpClient.put<unknown>(`/payment-methods/${id}`, { active: true })
    return paymentMethodMapper.toPaymentMethod(unwrap<PaymentMethodDto>(raw))
  }
}
